import { useState, useEffect, useRef } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import BarChartIcon from '@mui/icons-material/BarChart';
import Session from './shared/Session';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import LockClockIcon from '@mui/icons-material/LockClock';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApiIcon from '@mui/icons-material/Api';
import AppsIcon from '@mui/icons-material/Apps';


const Layout = (props: any) => {
    const [open, setOpen] = useState(Session.drawerOpen);
    const [userMenuAnchorElement, setUserMenuAnchorElement] = useState(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const debounceTimeout = useRef<any>(undefined);

    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = event.target.value;
        setSearchTerm(newSearchTerm);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        if (newSearchTerm.trim() !== '') {
            setLoading(true);
            debounceTimeout.current = setTimeout(() => {
                performSearch(newSearchTerm);
            }, 1000);
        } else {
            setLoading(false);
        }
    };

    const performSearch = async (term: string) => {
        setLoading(true);
        Session.api("POST", "/search", term, (response: any) => {
            console.log("Search results:", response);
            setLoading(false);
        });
    };


    function handleDrawerToggle() {
        setOpen(!open);
        localStorage.setItem("drawerOpen", (!open).toString());
    };

    const handleClick = (event: any) => {
        setUserMenuOpen(true);
        setUserMenuAnchorElement(event.currentTarget);
    }

    function handleClose() {
        setUserMenuOpen(false);
        setUserMenuAnchorElement(null);
    };

    function logout() {
        Session.clearToken()
        window.location.reload();
    }

    return (
        <div className="sj-root">
            <CssBaseline />
            <AppBar
                position="absolute"
                className="sj-appbar"
                sx={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: 2,
                    pl: 2
                }}
            >
                {Session.currentUser && Session.currentUser.isAdmin &&
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{
                            mt: 0.5,
                            mb: 0.5,
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            },
                            borderRadius: 1,
                            input: { color: 'white' },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'transparent',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'transparent',
                                },
                                height: '40px',
                            },
                            maxWidth: '300px',
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'white' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {loading && <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />}
                                </InputAdornment>
                            ),
                        }}
                    />
                }

                <Typography
                    component="h1"
                    variant="h6"
                    color="inherit"
                    noWrap
                    sx={{
                        flexGrow: 1,
                        textAlign: 'center',
                        position: 'absolute',
                        marginLeft: '50%',
                        transform: 'translate(-50%, 0%)'
                    }}
                >
                    <a className='inherit-color-and-decor' href="#" onClick={() => { Session.navigate("/"); }}>Simple Jay</a>
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center'}}>
                    {Session.currentUser ?
                        <>
                            <IconButton className="sj-profileLoginButton" color="inherit" onClick={handleClick}>
                                <AccessibilityNewIcon
                                    id="basic-button"
                                    aria-controls={userMenuOpen ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={userMenuOpen ? 'true' : undefined}
                                />
                            </IconButton>
                            <Menu
                                id="basic-menu"
                                anchorEl={userMenuAnchorElement}
                                open={userMenuOpen}
                                onClose={handleClose}
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button',
                                }}
                            >
                                <MenuItem onClick={() => { handleClose(); Session.navigate("users/" + Session.currentUser.id); }}>My Profile</MenuItem>
                                <MenuItem onClick={logout}>Logout</MenuItem>
                            </Menu>
                        </>
                        :
                        <IconButton className="sj-profileLoginButton" color="inherit" onClick={() => { Session.loginDialog.setOpen(true) }}>
                            <Badge
                                badgeContent={1}
                                color="secondary"
                                overlap="circular"
                            >
                                <NoAccountsIcon />
                            </Badge>
                        </IconButton>
                    }
                </Box>
            </AppBar>

            {Session.currentUser && Session.currentUser.isAdmin &&
                <Drawer
                    variant="permanent"
                    open={open}
                >
                    <List className={open ? "sj-drawer-open" : "sj-drawer-closed"}>
                        <ListItem className="sj-toggledrawer" onClick={handleDrawerToggle}>
                            <IconButton size="small">
                                {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                            </IconButton>
                        </ListItem>
                        <Tooltip title="API" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/api" onClick={(e) => { e.preventDefault(); Session.navigate("/api"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <ApiIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="API" />
                                </ListItem>
                            </a>
                        </Tooltip>
                        <Tooltip title="Tasks" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/tasks" onClick={(e) => { e.preventDefault(); Session.navigate("/tasks"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <BarChartIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Tasks" />
                                </ListItem>
                            </a>
                        </Tooltip>
                        <Tooltip title="Data" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/data" onClick={(e) => { e.preventDefault(); Session.navigate("/data"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <DataObjectIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Data" />
                                </ListItem>
                            </a>
                        </Tooltip>
                        <Tooltip title="Applications" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/applications" onClick={(e) => { e.preventDefault(); Session.navigate("/applications"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <AppsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Applications" />
                                </ListItem>
                            </a>
                        </Tooltip>
                        <Tooltip title="AI" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/ai" onClick={(e) => { e.preventDefault(); Session.navigate("/ai"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <PsychologyIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="AI" />
                                </ListItem>
                            </a>
                        </Tooltip>
                        <Tooltip title="Secrets" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/secrets" onClick={(e) => { e.preventDefault(); Session.navigate("/secrets"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <LockClockIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Secrets" />
                                </ListItem>
                            </a>
                        </Tooltip>
                        <Tooltip title="AI Mini Projects" disableHoverListener={open} placement='right'>
                            <a className='inherit-color-and-decor' href="/ai/mini_projects" onClick={(e) => { e.preventDefault(); Session.navigate("/ai/mini_projects"); }}>
                                <ListItem>
                                    <ListItemIcon>
                                        <AccountTreeIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Minis" />
                                </ListItem>
                            </a>
                        </Tooltip>
                    </List>
                    <Divider />
                </Drawer>
            }
            <main id="mainContent" className={open ? "sj-content-open" : "sj-content-closed"}>
                {props.children}
            </main>
        </div>
    );
}

export default Layout;
